import { z } from 'zod';
import { SourceSystemSchema } from './sourceSystem.js';
import {
  VisibilitySchema,
  AccessRoleSchema,
  ConversationShareSchema,
} from '../resource-sharing/index.js';

export const ConversationArchiveFilterSchema = z.enum(['exclude', 'only', 'include']);
export type ConversationArchiveFilter = z.infer<typeof ConversationArchiveFilterSchema>;

export const ConversationArchiveOriginSchema = z.enum(['manual', 'case_archived']);
export type ConversationArchiveOrigin = z.infer<typeof ConversationArchiveOriginSchema>;

/** Fully resolved personal organization state returned by pin/archive mutations. */
export const ResolvedConversationUserStateSchema = z.object({
  conversation_id: z.string().uuid(),
  is_pinned: z.boolean(),
  pinned_at: z.string().datetime().nullable(),
  is_archived: z.boolean(),
  archived_at: z.string().datetime().nullable(),
  archive_origin: ConversationArchiveOriginSchema.nullable(),
}).strict();
export type ResolvedConversationUserState = z.infer<
  typeof ResolvedConversationUserStateSchema
>;

export const SetConversationPinnedRequestSchema = z.object({ pinned: z.boolean() }).strict();
export const SetConversationArchivedRequestSchema = z.object({ archived: z.boolean() }).strict();
export const RenameConversationRequestSchema = z.object({
  conversation_title: z.string().trim().min(1).max(255),
}).strict();
/**
 * Corps de `POST /api/conversations`.
 *
 * `id` est l'UUID frappé par le client : avec lui, le backend crée le fil sur
 * le chemin du premier message temps réel, et rejouer la requête rend la même
 * ligne. Sans lui, le serveur choisit l'identifiant (chemin historique).
 */
export const CreateConversationRequestSchema = z.object({
  conversation_title: z.string().trim().min(1).max(255),
  case_id: z.string().uuid().optional(),
  vertex_ai_session_id: z.string().min(1).max(255).optional(),
  id: z.string().uuid().optional(),
}).strict();
export type CreateConversationRequest = z.infer<typeof CreateConversationRequestSchema>;
export type SetConversationPinnedRequest = z.infer<typeof SetConversationPinnedRequestSchema>;
export type SetConversationArchivedRequest = z.infer<typeof SetConversationArchivedRequestSchema>;
export type RenameConversationRequest = z.infer<typeof RenameConversationRequestSchema>;

/**
 * Wire shape d'une conversation renvoyée par le backend
 * (`GET /api/conversations/global`, `GET /api/cases/:caseId/conversations`).
 *
 * Tolérante aux champs optionnels : le backend actuel ne garantit pas
 * tous les champs (ConversationRepository.ts est en @ts-nocheck). Les frontends
 * durcissent à leur frontière si besoin.
 *
 * Resource-sharing (P4, T2) — champs enrichis exposés par `/conversations/global` :
 * - `owner_id` : propriétaire du fil (= `user_id` ; une conversation n'a pas de
 *   colonne `owner_id` dédiée, l'ownership vit dans `user_id`, cf. M135).
 * - `visibility` : `private` | `cabinet`.
 * - `my_role` : rôle RÉSOLU du demandeur (owner ∪ share ∪ visibilité), calculé
 *   côté SQL en parité avec `resolveConversationAccess`.
 * - `shares` : présent UNIQUEMENT quand `my_role === 'owner'` (owner-only ; le
 *   backend l'omet pour editor/viewer — ne jamais exposer les partages à un
 *   non-owner). Optionnels : les autres endpoints conversation ne les renvoient pas.
 */
export const ConversationWireSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid().optional(),
  case_id: z.string().uuid().nullable().optional(),
  case_name: z.string().nullable().optional(),
  conversation_title: z.string(),
  summary: z.string().nullable().optional(),
  source_system: SourceSystemSchema.optional(),
  message_count: z.number().int().nonnegative().optional(),
  created_at: z.string(),
  updated_at: z.string().optional(),
  last_activity: z.string().optional(),
  /** Technical compaction state; product archive state lives below. */
  is_active: z.boolean().optional(),
  // Resource-sharing (P4, T2)
  owner_id: z.string().uuid().nullable().optional(),
  visibility: VisibilitySchema.optional(),
  my_role: AccessRoleSchema.optional(),
  shares: z.array(ConversationShareSchema).optional(),
  // Personal organization is resolved for the requesting user only. It stays
  // optional while callers migrate, so older conversation endpoints remain valid.
  ...ResolvedConversationUserStateSchema
    .omit({ conversation_id: true })
    .partial()
    .shape,
});

export type ConversationWire = z.infer<typeof ConversationWireSchema>;
